import { useState } from 'react';
import type { CalendarEvent, CalendarInfo, EventInput, Repeat } from 'zitimail-shared-types';

import { calendar } from '../api/calendar';
import { askToNotifyReminders } from '../mail/notify';
import { Availability } from './Availability';
import { addDays, DAY, localInput } from './dates';
import { reminderChoices, reminderLabel } from './reminders';

export interface Draft {
  /** Present when editing. */
  readonly event?: CalendarEvent;
  readonly start: Date;
  readonly end: Date;
  readonly allDay: boolean;
  /** A title already typed, e.g. in the quick-create popover. */
  readonly title?: string;
}

const STATUS: Record<string, string> = {
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  TENTATIVE: 'maybe',
  'NEEDS-ACTION': 'no answer yet',
};

/**
 * Create or edit an event. Someone else's invitation opens read-only: the
 * organizer owns the event, and the invitee answers from the email.
 */
export function EventEditor({
  draft,
  calendars,
  me,
  onClose,
  onSaved,
}: {
  draft: Draft;
  calendars: CalendarInfo[];
  me: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const existing = draft.event;
  const readOnly = Boolean(existing?.organizer && existing.organizer !== me.toLowerCase());
  const [title, setTitle] = useState(existing?.title ?? draft.title ?? '');
  const [allDay, setAllDay] = useState(draft.allDay);
  const [start, setStart] = useState(localInput(draft.start));
  const [end, setEnd] = useState(localInput(draft.end));
  const [startDay, setStartDay] = useState(localInput(draft.start).slice(0, 10));
  const [endDay, setEndDay] = useState(
    localInput(addDays(draft.end, draft.allDay ? -1 : 0)).slice(0, 10),
  );
  const [location, setLocation] = useState(existing?.location ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [attendees, setAttendees] = useState(
    (existing?.attendees ?? []).map((a) => a.email).join(', '),
  );
  const [repeat, setRepeat] = useState<Repeat>(existing?.repeat ?? 'none');
  // For one occurrence of a series: change just it, or the whole series.
  const [scope, setScope] = useState<'one' | 'all'>('one');
  const [reminder, setReminder] = useState<number | undefined>(existing?.reminderMinutes);
  const [target, setTarget] = useState(existing?.calendar ?? calendars[0]?.href ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guests = attendees
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean);

  const times = (): { start: string; end: string } => {
    if (allDay) {
      return {
        start: `${startDay}T00:00:00.000Z`,
        // The form shows the last day; iCalendar wants the day after.
        end: new Date(Date.parse(`${endDay}T00:00:00.000Z`) + DAY).toISOString(),
      };
    }
    return { start: new Date(start).toISOString(), end: new Date(end).toISOString() };
  };

  const input = (): EventInput => ({
    calendar: target,
    title,
    ...times(),
    allDay,
    location,
    description,
    repeat,
    attendees: guests,
    ...(reminder !== undefined ? { reminderMinutes: reminder } : {}),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Not saved.');
    } finally {
      setBusy(false);
    }
  };

  /** Saves as the scope says: a new event, the event, one occurrence, or the series. */
  const save = () => {
    if (!existing) {
      return calendar.create(input());
    }
    if (!existing.recurring) {
      return calendar.update(existing.href, existing.etag, input());
    }
    if (scope === 'one' && existing.recurrenceId) {
      return calendar.update(
        existing.href,
        existing.etag,
        { ...input(), repeat: 'none' },
        existing.recurrenceId,
      );
    }
    // The whole series: the change made to this occurrence, applied to the
    // series' own first occurrence.
    const t = times();
    const shift = Date.parse(t.start) - Date.parse(existing.start);
    const length = Date.parse(t.end) - Date.parse(t.start);
    const first = Date.parse(existing.seriesStart ?? existing.start) + shift;
    return calendar.update(existing.href, existing.etag, {
      ...input(),
      start: new Date(first).toISOString(),
      end: new Date(first + length).toISOString(),
    });
  };

  return (
    <div className="modal-scrim" role="presentation" onClick={onClose}>
      <form
        className="event-editor"
        role="dialog"
        aria-label={existing ? 'Event' : 'New event'}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          void run(save);
        }}
      >
        <div className="compose-head">
          <strong>{existing ? (readOnly ? 'Invitation' : 'Edit event') : 'New event'}</strong>
          <button type="button" className="adm-copy" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          aria-label="Title"
          required
          disabled={readOnly}
          autoFocus
        />
        <label className="check">
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            disabled={readOnly}
          />{' '}
          All day
        </label>
        {allDay ? (
          <div className="row2">
            <input
              type="date"
              value={startDay}
              onChange={(e) => setStartDay(e.target.value)}
              aria-label="First day"
              required
              disabled={readOnly}
            />
            <input
              type="date"
              value={endDay}
              onChange={(e) => setEndDay(e.target.value)}
              aria-label="Last day"
              required
              disabled={readOnly}
            />
          </div>
        ) : (
          <div className="row2">
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              aria-label="Starts"
              required
              disabled={readOnly}
            />
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              aria-label="Ends"
              required
              disabled={readOnly}
            />
          </div>
        )}
        {existing?.recurring && !readOnly && (
          <div className="scope" role="radiogroup" aria-label="Change">
            <label>
              <input type="radio" checked={scope === 'one'} onChange={() => setScope('one')} /> This
              event
            </label>
            <label>
              <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} /> All
              events in the series
            </label>
          </div>
        )}
        {!readOnly && (!existing?.recurring || scope === 'all') && (
          <select
            value={repeat}
            onChange={(e) => setRepeat(e.target.value as Repeat)}
            aria-label="Repeat"
          >
            <option value="none">Does not repeat</option>
            <option value="daily">Every day</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
            <option value="yearly">Every year</option>
            {existing?.repeat === 'custom' && (
              <option value="custom">Custom, as set in another app</option>
            )}
          </select>
        )}
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Location"
          aria-label="Location"
          disabled={readOnly}
        />
        {readOnly ? (
          <ul className="guests">
            <li>Organizer: {existing?.organizer}</li>
            {existing?.attendees.map((a) => (
              <li key={a.email}>
                {a.name ?? a.email} — {STATUS[a.partstat]}
              </li>
            ))}
          </ul>
        ) : (
          <>
            <input
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="Invite (addresses, comma-separated)"
              aria-label="Guests"
            />
            {guests.length > 0 &&
              (() => {
                const t = (() => {
                  try {
                    return times();
                  } catch {
                    return null;
                  }
                })();
                return t && !allDay ? (
                  <Availability guests={guests} start={new Date(t.start)} end={new Date(t.end)} />
                ) : null;
              })()}
            {existing && existing.attendees.length > 0 && (
              <ul className="guests">
                {existing.attendees.map((a) => (
                  <li key={a.email}>
                    {a.name ?? a.email} — {STATUS[a.partstat]}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <select
          value={reminder === undefined ? '' : String(reminder)}
          onChange={(e) => {
            setReminder(e.target.value === '' ? undefined : Number(e.target.value));
            // Asked here, on the choice: the browser lets only a click ask.
            if (e.target.value !== '') {
              askToNotifyReminders();
            }
          }}
          aria-label="Alert"
          disabled={readOnly}
        >
          {reminderChoices(existing?.reminderMinutes).map((minutes) => (
            <option key={String(minutes)} value={minutes === undefined ? '' : String(minutes)}>
              {minutes === undefined
                ? 'No alert'
                : `Alert ${reminderLabel(minutes).replace(/^A/, 'a')}`}
            </option>
          ))}
        </select>
        {calendars.length > 1 && !existing && (
          <select value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Calendar">
            {calendars.map((c) => (
              <option key={c.href} value={c.href}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="Notes"
          aria-label="Notes"
          disabled={readOnly}
        />
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
        <div className="compose-foot">
          {!readOnly && (
            <button type="submit" className="btn" disabled={busy} style={{ width: 'auto' }}>
              {busy ? 'Saving…' : guests.length && !existing ? 'Save and send invitations' : 'Save'}
            </button>
          )}
          {existing && existing.recurring && (
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              style={{ width: 'auto' }}
              onClick={() =>
                void run(() => calendar.remove(existing.href, existing.etag, existing.recurrenceId))
              }
            >
              Delete this occurrence
            </button>
          )}
          {existing && (
            <button
              type="button"
              className="btn ghost"
              disabled={busy}
              style={{ width: 'auto' }}
              onClick={() => void run(() => calendar.remove(existing.href, existing.etag))}
            >
              {existing.recurring ? 'Delete the series' : 'Delete'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
